import config from './config';

export interface UserPermissions {
  role: string | null;
  role_hierarchy: number | null;
  permissions: string[];
  app_id: string;
}

export interface UserInfo {
  userId: number;
  email: string;
  name: string;
  profilePictureUrl?: string;
  roleName: string;
  roleDisplayName: string;
  assignedAt: string;
  assignedBy?: number;
  lastLoginAt?: string;
}

export interface InvitationInfo {
  id: number;
  email: string;
  roleName: string;
  invitedAt: string;
  expiresAt: string;
  status: string;
  invitedByName: string;
}

export interface InviteUserRequest {
  email: string;
  roleName: string;
}

export interface InviteUserResponse {
  success: boolean;
  message: string;
  userId: number;
  email: string;
  roleName: string;
  roleDisplayName: string;
  isNewUser: boolean;
}

export interface UpdateRoleRequest {
  newRoleName: string;
}

export interface UpdateRoleResponse {
  success: boolean;
  message: string;
  userId: number;
  email: string;
  oldRoleName: string;
  newRoleName: string;
  newRoleDisplayName: string;
}

// Get user permissions for a specific application
export const getMyPermissions = async (token: string, appId: string): Promise<UserPermissions> => {
  const response = await fetch(`${config.authServiceUrl}/api/users/me/permissions?app_id=${appId}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch permissions');
  }

  return await response.json();
};

// List all users in the current account/app
export const listUsers = async (token: string): Promise<UserInfo[]> => {
  const response = await fetch(`${config.authServiceUrl}/api/users`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }

  return await response.json();
};

// List pending invitations for the current account/app
export const listInvitations = async (token: string): Promise<InvitationInfo[]> => {
  const response = await fetch(`${config.authServiceUrl}/api/users/invitations`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch invitations');
  }

  return await response.json();
};

// Invite a user to the application
export const inviteUser = async (token: string, request: InviteUserRequest): Promise<InviteUserResponse> => {
  const response = await fetch(`${config.authServiceUrl}/api/users/invite`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to invite user');
  }

  return await response.json();
};

// Update a user's role
export const updateUserRole = async (
  token: string,
  userId: number,
  newRoleName: string
): Promise<UpdateRoleResponse> => {
  const response = await fetch(`${config.authServiceUrl}/api/users/${userId}/role`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ newRoleName }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to update role');
  }

  return await response.json();
};

// Remove a user from the application
export const deleteUser = async (token: string, userId: number): Promise<{ message: string }> => {
  const response = await fetch(`${config.authServiceUrl}/api/users/${userId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to delete user');
  }

  return await response.json();
};

// OAuth Client Management Types
export interface CreateClientRequest {
  clientName: string;
  description?: string;
  clientType?: string;
  grantTypes?: string;
  allowedScopes: string;
  expiresAt?: string;
}

export interface CreateClientResponse {
  clientId: string;
  clientSecret: string;
  clientName: string;
  description?: string;
  allowedScopes: string;
  grantTypes: string;
  createdAt: string;
  message: string;
}

export interface UpdateClientRequest {
  clientName?: string;
  description?: string;
  allowedScopes?: string;
}

export interface ClientInfo {
  id: number;
  clientId: string;
  clientName: string;
  description?: string;
  clientType: string;
  grantTypes: string;
  allowedScopes: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
}

export interface RotateSecretResponse {
  clientId: string;
  clientSecret: string;
  message: string;
}

export interface ClientStats {
  clientId: string;
  clientName: string;
  totalTokensIssued: number;
  activeTokens: number;
  revokedTokens: number;
  expiredTokens: number;
  lastTokenIssuedAt?: string;
  lastUsedAt?: string;
}

// Create a new OAuth client
export const createOAuthClient = async (
  token: string,
  request: CreateClientRequest
): Promise<CreateClientResponse> => {
  const response = await fetch(`${config.authServiceUrl}/api/clients`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to create OAuth client');
  }

  return await response.json();
};

// List all OAuth clients for the authenticated user's account
export const listOAuthClients = async (token: string): Promise<ClientInfo[]> => {
  const response = await fetch(`${config.authServiceUrl}/api/clients`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch OAuth clients');
  }

  return await response.json();
};

// Get details of a specific OAuth client
export const getOAuthClient = async (token: string, clientId: string): Promise<ClientInfo> => {
  const response = await fetch(`${config.authServiceUrl}/api/clients/${clientId}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to fetch OAuth client');
  }

  return await response.json();
};

// Update an OAuth client's configuration
export const updateOAuthClient = async (
  token: string,
  clientId: string,
  request: UpdateClientRequest
): Promise<ClientInfo> => {
  const response = await fetch(`${config.authServiceUrl}/api/clients/${clientId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to update OAuth client');
  }

  return await response.json();
};

// Deactivate an OAuth client
export const deactivateOAuthClient = async (
  token: string,
  clientId: string
): Promise<{ message: string; clientId: string }> => {
  const response = await fetch(`${config.authServiceUrl}/api/clients/${clientId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to deactivate OAuth client');
  }

  return await response.json();
};

// Activate (reactivate) an OAuth client
export const activateOAuthClient = async (
  token: string,
  clientId: string
): Promise<{ message: string; clientId: string }> => {
  const response = await fetch(`${config.authServiceUrl}/api/clients/${clientId}/activate`, {
    method: 'PATCH',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to activate OAuth client');
  }

  return await response.json();
};

// Rotate the client secret
export const rotateOAuthClientSecret = async (
  token: string,
  clientId: string
): Promise<RotateSecretResponse> => {
  const response = await fetch(`${config.authServiceUrl}/api/clients/${clientId}/rotate-secret`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to rotate client secret');
  }

  return await response.json();
};

// Get token usage statistics for a client
export const getOAuthClientStats = async (
  token: string,
  clientId: string
): Promise<ClientStats> => {
  const response = await fetch(`${config.authServiceUrl}/api/clients/${clientId}/stats`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to fetch client stats');
  }

  return await response.json();
};

// Scope Management Types
export interface ScopeInfo {
  name: string;
  description: string;
  action: string;
}

export interface ScopeGroup {
  resource: string;
  displayName: string;
  scopes: ScopeInfo[];
}

// Get all available OAuth scopes grouped by resource
export const getScopesList = async (token: string): Promise<ScopeGroup[]> => {
  const response = await fetch(`${config.authServiceUrl}/api/scopes`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch scopes');
  }

  return await response.json();
};
